// The search routes' client (module 10 U227; pipeline F3, B1953), mirroring apps/api/src/routes/search.ts field for
// field. Its own file, as deals/api.ts is, because apps/portal/src/api.ts is records-page's.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export const SEARCH_KINDS = ['companies', 'people', 'deals', 'meetings', 'tasks', 'chats'] as const;
export type SearchKind = (typeof SEARCH_KINDS)[number];

export interface SearchHit { id: string; title: string; detail: string | null; when: string | null }
export interface SearchGroup { kind: SearchKind; count: number; hits: SearchHit[] }
interface StoreRef { tenantId: string; kind: 'commercial' | 'personal'; name: string }

/** GET /api/search/records. `refused` and `answer` only for a never-said subject (R44), when every group is empty. */
export interface RecordsAnswer { store: StoreRef; q: string; groups: SearchGroup[]; refused?: string; answer?: string }

/** Where a passage opens: the reader's triage item at the message, their meeting, or their chat. */
export type PassageOpens = { page: 'item'; id: string } | { page: 'meeting'; id: string } | { page: 'chat'; id: string };
export interface Passage {
  signalId: string; kind: string; direction: string; occurredAt: string; subject: string | null; domainTier: string | null;
  threadId: string | null; participants: string | null; matters: string | null; passage: string; score: number;
  strictHit: boolean; nameHit: boolean; opens: PassageOpens | null;
}
/** GET /api/search/corpus: what Cobrain read, classified only; or the refusal, with nothing searched. */
export type CorpusAnswer =
  | { store: StoreRef; q: string; refused: string; answer: string; terms: []; passages: []; records: [] }
  | { store: StoreRef; q: string; refused?: undefined; terms: string[]; passages: Passage[]; records: unknown[] };

/** A message of the reader's own that Cobrain has not read yet (module 05 R62, F4). It opens nothing in Cobrain. */
export interface NotYetReadPassage {
  signalId: string; kind: string; direction: string | null; occurredAt: string; subject: string | null; participants: string | null; passage: string;
}
/** GET /api/search/not-yet-read: a count and the newest few; for a never-said subject (R44), nothing, with the refusal. */
export interface NotYetReadAnswer { store: StoreRef; q: string; count: number; passages: NotYetReadPassage[]; refused?: string; answer?: string }

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json().then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null)).catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

const qs = (o: Record<string, string | number | undefined>) =>
  Object.entries(o).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');

export const search = {
  records: (store: string, q: string, opts: { kinds?: readonly SearchKind[]; each?: number; signal?: AbortSignal } = {}) =>
    fetch(`/api/search/records?${qs({ store, q, kinds: opts.kinds?.join(','), each: opts.each })}`, { credentials: 'same-origin', ...(opts.signal ? { signal: opts.signal } : {}) })
      .then((r) => json<RecordsAnswer>(r)),
  corpus: (store: string, q: string, signal?: AbortSignal) =>
    fetch(`/api/search/corpus?${qs({ store, q })}`, { credentials: 'same-origin', ...(signal ? { signal } : {}) }).then((r) => json<CorpusAnswer>(r)),
  notYetRead: (store: string, q: string, opts: { each?: number; signal?: AbortSignal } = {}) =>
    fetch(`/api/search/not-yet-read?${qs({ store, q, each: opts.each })}`, { credentials: 'same-origin', ...(opts.signal ? { signal: opts.signal } : {}) })
      .then((r) => json<NotYetReadAnswer>(r)),
};
